import { requestServise } from "@/shared";
import $api from "@/shared/http";
import { RootState } from "@/app/appStore";
import { toApiError } from "@/shared/http/types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { isAxiosError } from "axios";
import {
  CartFilterType,
  CartProductType,
  DealerRemainsProductType,
  ItemToCart,
} from "../../model/types";
import {
  buildBasketQtyMap,
  flattenDealerRemainsItems,
  RemainsBasketQtyMap,
} from "../../lib/buildBasketQtyMap";

export const addRemainsItemsCart = requestServise.post<{
  items: ItemToCart[];
}>("cart/addRemainsItemsCart", "/basket/remains/items");

export const deleteRemainsCart = requestServise.delete(
  "cart/deleteRemainsCart",
  "/basket/remains",
);

export const fetchRemainsCart = createAsyncThunk<
  { items: CartProductType[]; totalCount: number },
  void,
  { state: RootState; rejectValue: string }
>("cart/fetchRemainsCart", async (_, { getState, rejectWithValue, signal }) => {
  const state = getState().cart;
  const body = {
    order: state.remainsOrder,
    pagination: {
      pageSize: state.remainsPageSize,
      pageNumber: state.remainsPageNumber,
    },
    filters: [{ fields: state.selectedRemainsFilters, type: "eq" }],
  };
  try {
    const response = await $api.post("/basket/remains", JSON.stringify(body), {
      signal,
    });
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error("Server error");
    }
  } catch (error) {
    if (signal.aborted || error?.code === "ERR_CANCELED") {
      return rejectWithValue("canceled");
    }
    return rejectWithValue(error.message);
  }
});

// Корзины «остатки СКАД» и «выкуп остатков партнёров» целиком — для
// бейджей «уже в корзине» в таблице остатков
const BASKET_QTY_PAGE_SIZE = 1000;

const fetchWholeBasket = async <T>(
  url: string,
  signal: AbortSignal,
): Promise<T[]> => {
  const body = {
    order: { type: "asc", field: "Code" },
    pagination: { pageSize: BASKET_QTY_PAGE_SIZE, pageNumber: 1 },
    filters: [] as CartFilterType,
  };
  try {
    const response = await $api.post<{ items: T[] | null }>(url, body, {
      signal,
    });
    return response.data?.items ?? [];
  } catch (error) {
    // 404 — корзины ещё нет
    if (isAxiosError(error) && error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

export const fetchRemainsBasketQuantities = createAsyncThunk<
  RemainsBasketQtyMap,
  void,
  { rejectValue: string }
>(
  "cart/fetchRemainsBasketQuantities",
  async (_, { rejectWithValue, signal }) => {
    try {
      const [skadItems, dealerItems] = await Promise.all([
        fetchWholeBasket<CartProductType>("/basket/remains", signal),
        fetchWholeBasket<DealerRemainsProductType>(
          "/basket/dealer/remains",
          signal,
        ),
      ]);
      return buildBasketQtyMap([
        ...skadItems,
        ...flattenDealerRemainsItems(dealerItems),
      ]);
    } catch (error) {
      if (
        signal.aborted ||
        (isAxiosError(error) && error.code === "ERR_CANCELED")
      ) {
        return rejectWithValue("canceled");
      }
      return rejectWithValue(toApiError(error).message);
    }
  },
);
